/**
 * Kit-template domain types shared between server and web.
 *
 * A kit template maps a job role (e.g. "M&E Engineer") or a role
 * category (e.g. "Engineer") to the list of items that person is
 * expected to receive across the stations on mobilisation day. A
 * person's effective kit is the category baseline merged with their
 * role's template, role winning on clashes. The list is editable up
 * to the day before by admins / coordinators.
 *
 * The actual issuance (laptop, PPE, van keys etc) is recorded as
 * events in the events table; templates only describe expectations.
 */

export const STATIONS = ['IT', 'PPE', 'Tools', 'Fleet', 'DBSHR'] as const;
export type Station = (typeof STATIONS)[number];

/**
 * Stations the user actually sees and acts on right now. DBSHR is
 * supported by the data model but hidden from menus, progress
 * roll-ups, and the completion check until HR has a concrete use
 * case for it. Re-enable by adding 'DBSHR' back to this tuple.
 *
 * Use STATIONS when you need the full schema (event payload
 * validation, station_settings keys, asset categories). Use
 * VISIBLE_STATIONS for anything user-facing or anything that
 * decides "is this person done".
 */
export const VISIBLE_STATIONS = ['IT', 'PPE', 'Tools', 'Fleet'] as const;
export type VisibleStation = (typeof VISIBLE_STATIONS)[number];

/**
 * Display-friendly station labels. Internal IDs (Station enum) stay
 * stable for URLs, DB keys, role names, asset categories — only the
 * label changes when the operator-facing wording moves on. PPE is
 * shown as "PPE / Uniform" because that station now covers both
 * categories; the underlying station_id stays 'PPE'.
 */
export const STATION_LABELS: Record<Station, string> = {
  IT: 'IT',
  PPE: 'PPE / Uniform',
  Tools: 'Tools',
  Fleet: 'Fleet',
  DBSHR: 'DBS / HR',
};

export function stationLabel(station: Station): string {
  return STATION_LABELS[station] ?? station;
}

/**
 * The station role that grants access to a station's desk and its kit
 * issuance (e.g. 'IT' -> 'station_it'). Mirrors the ROLES.STATION_*
 * identifiers, so the nav and the pre-day issue picker map roles to
 * stations the same way rather than each hand-rolling the string.
 */
export function stationRoleFor(station: Station): string {
  return `station_${station.toLowerCase()}`;
}

/**
 * Sub-classification of items at the PPE / Uniform station so admins
 * can distinguish what's safety kit (boots, hi-vis, hard hat) from
 * what's branded clothing (polo, fleece). Some inductees get both,
 * some only get uniform. The field is optional — undefined means
 * "unspecified, treat as PPE" for backwards compatibility with
 * existing kit templates.
 *
 * Only meaningful when station === 'PPE'. The field is silently
 * ignored for items at other stations.
 */
export const KIT_CATEGORIES = ['PPE', 'Uniform'] as const;
export type KitCategory = (typeof KIT_CATEGORIES)[number];

/**
 * Links a kit item to a sizing-form row so its size comes from the person's
 * submission, keyed on stable identifiers rather than the display name (which
 * drifts: "Polo Shirts" vs "Polo Shirt - Black"). When set, the station
 * pre-fills the submitted size and drops the item if the person opted out via
 * that row's gate (e.g. "I'll buy my own boots"). The canonical item name,
 * category and gate all come from the linked row; the kit item still owns the
 * quantity (so one role can get 2 polos and another 3).
 */
export interface KitItemSizeRef {
  form_key: string;
  field_key: string;
  row_key: string;
}

export interface KitItem {
  station: Station;
  /** Human-readable item name, e.g. "Laptop", "Hi-vis vest". */
  name: string;
  /** Default 1. Capped at 99 to keep the form sensible. */
  quantity: number;
  /** True for items where a size matters (boots, hi-vis), so reception/PPE prompt for one. */
  size_required: boolean;
  /**
   * PPE-station only: 'PPE' (safety kit) or 'Uniform' (branded
   * clothing). Undefined for non-PPE items and for legacy PPE items
   * that haven't been categorised yet.
   */
  category?: KitCategory;
  /** Optional link to a sizing-form row; see KitItemSizeRef. */
  size_ref?: KitItemSizeRef;
}

export interface KitTemplate {
  id: string;
  /** Denormalised display mirror of the target's name (role or category). */
  position: string;
  /** Exactly one of role_id / category_id is set: the kit is either specific
   * to one role, or the shared baseline for every role in a category. */
  role_id: string | null;
  category_id: string | null;
  items: readonly KitItem[];
  created_at: string; // ISO 8601
}

/** Shape used by the create / update endpoints. Exactly one of role_id /
 * category_id; the server resolves it to the display name. */
export interface KitTemplateInput {
  role_id?: string | null;
  category_id?: string | null;
  items: readonly KitItem[];
}
